import React from 'react';
import { COLORS, RADIUS, SPACING, TYPOGRAPHY, SHADOWS, ANIMATION } from '../../config/branding';

/**
 * Button Component - Consistent button styling across the application
 *
 * @param {string} variant - Button variant: 'primary', 'secondary', 'success', 'warning', 'error', 'ghost', 'outline'
 * @param {string} size - Button size: 'sm', 'md', 'lg'
 * @param {boolean} loading - Show loading spinner
 * @param {boolean} disabled - Disable button
 * @param {boolean} fullWidth - Make button full width
 * @param {React.ReactNode} icon - Icon component to display
 * @param {string} iconPosition - Icon position: 'left', 'right'
 * @param {function} onClick - Click handler
 * @param {string} type - Button type: 'button', 'submit', 'reset'
 */
const Button = ({
  children,
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled = false,
  fullWidth = false,
  icon = null,
  iconPosition = 'left',
  onClick,
  type = 'button',
  className = '',
  ...props
}) => {
  // Variant styles
  const variantStyles = {
    primary: {
      backgroundColor: COLORS.primary,
      color: COLORS.text.inverse,
      border: 'none',
      hover: {
        backgroundColor: COLORS.primaryHover,
      }
    },
    secondary: {
      backgroundColor: COLORS.secondary,
      color: COLORS.text.inverse,
      border: 'none',
      hover: {
        backgroundColor: COLORS.secondaryHover,
      }
    },
    success: {
      backgroundColor: COLORS.success,
      color: COLORS.text.inverse,
      border: 'none',
      hover: {
        backgroundColor: COLORS.successHover,
      }
    },
    warning: {
      backgroundColor: COLORS.warning,
      color: COLORS.white,
      border: 'none',
      hover: {
        backgroundColor: COLORS.warningHover,
      }
    },
    error: {
      backgroundColor: COLORS.error,
      color: COLORS.text.inverse,
      border: 'none',
      hover: {
        backgroundColor: COLORS.errorHover,
      }
    },
    ghost: {
      backgroundColor: 'transparent',
      color: COLORS.text.primary,
      border: 'none',
      hover: {
        backgroundColor: COLORS.gray[100],
      }
    },
    outline: {
      backgroundColor: 'transparent',
      color: COLORS.primary,
      border: `2px solid ${COLORS.primary}`,
      hover: {
        backgroundColor: COLORS.primaryLight,
      }
    }
  };

  // Size styles
  const sizeStyles = {
    sm: {
      padding: `${SPACING.xs} ${SPACING.md}`,
      fontSize: TYPOGRAPHY.fontSize.sm,
      height: '32px',
    },
    md: {
      padding: `${SPACING.sm} ${SPACING.lg}`,
      fontSize: TYPOGRAPHY.fontSize.base,
      height: '40px',
    },
    lg: {
      padding: `${SPACING.md} ${SPACING.xl}`,
      fontSize: TYPOGRAPHY.fontSize.lg,
      height: '48px',
    }
  };

  const currentVariant = variantStyles[variant];
  const currentSize = sizeStyles[size];

  const baseStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: SPACING.sm,
    fontFamily: TYPOGRAPHY.fontFamily.sans,
    fontWeight: TYPOGRAPHY.fontWeight.medium,
    borderRadius: RADIUS.lg,
    cursor: disabled || loading ? 'not-allowed' : 'pointer',
    transition: `all ${ANIMATION.normal} ease-in-out`,
    boxShadow: SHADOWS.sm,
    width: fullWidth ? '100%' : 'auto',
    opacity: disabled || loading ? 0.6 : 1,
    outline: 'none',
    ...currentSize,
    ...currentVariant,
    border: currentVariant.border,
  };

  const handleClick = (e) => {
    if (disabled || loading) return;
    if (onClick) onClick(e);
  };

  return (
    <button
      type={type}
      style={baseStyle}
      onClick={handleClick}
      disabled={disabled || loading}
      className={`button button-${variant} button-${size} ${className}`}
      onMouseEnter={(e) => {
        if (!disabled && !loading) {
          Object.assign(e.target.style, currentVariant.hover);
        }
      }}
      onMouseLeave={(e) => {
        if (!disabled && !loading) {
          e.target.style.backgroundColor = currentVariant.backgroundColor;
        }
      }}
      {...props}
    >
      {loading && (
        <svg
          style={{ animation: 'spin 1s linear infinite' }}
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
        >
          <circle cx="12" cy="12" r="10" strokeWidth="3" stroke="currentColor" strokeOpacity="0.25" />
          <path d="M12 2a10 10 0 0 1 10 10" strokeWidth="3" strokeLinecap="round" />
          <style>{`
            @keyframes spin {
              to { transform: rotate(360deg); }
            }
          `}</style>
        </svg>
      )}
      {icon && iconPosition === 'left' && !loading && icon}
      {children}
      {icon && iconPosition === 'right' && !loading && icon}
    </button>
  );
};

export default Button;
